/**
 * How a speciallægehenvisning (XREF06) reaches a receiver, and which receivers
 * the free-choice scheme rules out. Same rules as `api/src/sor/delivery.ts` in
 * Clinio Communicate, which enforces them again when the letter is built.
 *
 * MedCom (REF06-Send, step 3.5, review 13-10-2026):
 *  - Reasons 1, 3, 4, 5: a receiver that only takes REF06 (EDIFACT) stays
 *    selectable. The XREF06 is converted to EDIFACT for it.
 *  - Reason 6: those REF06-only receivers are filtered out. Henvisningshotellet
 *    (DNHF) is also blocked. A specific speciality doctor that reads XREF06
 *    must still be searchable.
 */

/** SOR id of "Henvisningshotellet". Every DNHF speciality unit has it as parent. */
export const DNHF_PARENT_SOR_ID = '1202831000016009';

/** Referral reason (henvisningsdiagnose) for which DNHF may not be addressed. */
export const DNHF_BLOCKED_DIAGNOSE_CODE = '6';

/** Specialist search always asks for both XML and EDIFACT receivers. */
export const XREF06_LOOKUP_RECEIVES = ['XREF06', 'REF06'] as const;

export function xref06LookupReceivesQuery(
  receives: readonly string[] = XREF06_LOOKUP_RECEIVES,
): string {
  return receives.join(',');
}

export type Xref06Receiver = {
  ediReceiveTypes: readonly string[];
  parentSorId: string | null;
};

/** The receiver reads EDIFACT only, so the XREF06 is converted for it. */
export function receivesEdifactOnly(receiver: Xref06Receiver): boolean {
  return !receiver.ediReceiveTypes.includes('XREF06') && receiver.ediReceiveTypes.includes('REF06');
}

export function isDnhfUnit(receiver: Xref06Receiver): boolean {
  return receiver.parentSorId === DNHF_PARENT_SOR_ID;
}

/**
 * True when this receiver may not be chosen with this referral reason.
 * Independent of which of the two the clinician picks first.
 */
export function dnhfBlocked(receiver: Xref06Receiver, diagnoseCode: string | null): boolean {
  return diagnoseCode === DNHF_BLOCKED_DIAGNOSE_CODE && isDnhfUnit(receiver);
}

/** True when a REF06-only receiver may not be chosen with this referral reason. */
export function ref06OnlyBlocked(receiver: Xref06Receiver, diagnoseCode: string | null): boolean {
  return diagnoseCode === DNHF_BLOCKED_DIAGNOSE_CODE && receivesEdifactOnly(receiver);
}

/** True when this receiver may not be chosen with this referral reason. */
export function xref06ReceiverBlocked(
  receiver: Xref06Receiver,
  diagnoseCode: string | null,
): boolean {
  return dnhfBlocked(receiver, diagnoseCode) || ref06OnlyBlocked(receiver, diagnoseCode);
}

/** REF06-only receivers are omitted from the picker only at reason 6. */
export function visibleXref06Receivers<T extends Xref06Receiver>(
  receivers: readonly T[],
  diagnoseCode: string | null,
): T[] {
  return receivers.filter((receiver) => !ref06OnlyBlocked(receiver, diagnoseCode));
}
